import { valibotResolver } from "@hookform/resolvers/valibot";
import {
  DotsThreeVertical,
  PencilSimpleLine,
  Trash,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import FormField from "common/components/ui/FormField";
import useLeadNotes from "common/datahooks/useLeadNotes";
import { formatLastUpdateAt } from "common/helpers/utils";
import { NoteSchema, type NoteSchemaType } from "common/schemas";
import type { Note as NoteType } from "common/types";

import TextArea from "../../ui/TextArea";

import type React from "react";

const maxVisibleCharacters = 200;
export default function Note({
  note,
  profileId,
  onDelete,
}: {
  profileId: string;
  note: NoteType;
  onDelete: () => void;
}) {
  const { id, text, edited_at: editedAt, created_at: createdAt } = note;
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(
    text.length < maxVisibleCharacters,
  );

  const { updateLeadNote, isUpdatingLeadNote } = useLeadNotes();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<NoteSchemaType>({
    defaultValues: { text },
    resolver: valibotResolver(NoteSchema),
  });

  async function onSubmit({ text: newText }: NoteSchemaType) {
    if (newText.trim() !== text) {
      updateLeadNote({ profileId, noteId: id, newText })
        .then(() => {
          reset({ text: newText });
          setIsEditing(false);
          setIsExpanded(text.length < maxVisibleCharacters);
        })
        .catch(() => {
          toast.error("Error saving note");
        });
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit(onSubmit)();
    }
  }

  if (isEditing) {
    return (
      <div className="flex flex-col gap-y-2">
        <FormField label="" htmlFor="" error={errors.text?.message}>
          <TextArea
            {...register("text")}
            hasError={!!errors.text?.message}
            autoFocus
            minRows={4}
            maxRows={6}
            placeholder="Write something..."
            onKeyDown={onKeyDown}
            disabled={isUpdatingLeadNote}
          />
        </FormField>

        <div className="flex justify-end gap-x-2">
          <Button
            variant="outline-black"
            disabled={isUpdatingLeadNote}
            onClick={() => setIsEditing(false)}
          >
            Discard
          </Button>

          <Button
            variant="secondary-purple"
            isLoading={isUpdatingLeadNote}
            onClick={handleSubmit(onSubmit)}
          >
            Save
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-y-2">
      <div className="flex flex-1 flex-col overflow-hidden rounded-xl bg-black-100 pb-2.5 text-sm-medium">
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className="float-right"
                intent="iconOnly"
                variant="secondary-black"
                size="xs"
              >
                <DotsThreeVertical />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent side="left" className="z-aboveDialog">
              <DropdownMenuList>
                <DropdownMenuItem onClick={() => setIsEditing(true)}>
                  <PencilSimpleLine />
                  Edit note
                </DropdownMenuItem>

                <DropdownMenuItem onClick={onDelete} variant="danger">
                  <Trash />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuList>
            </DropdownMenuContent>
          </DropdownMenu>
          <p className="px-2.5 pt-2.5">
            {isExpanded ? text : `${text.slice(0, maxVisibleCharacters)}...`}
          </p>
        </div>
        <RenderIf condition={!isExpanded}>
          <button
            type="button"
            className="ml-auto mr-2.5 text-xs text-purple-600"
            onClick={() => setIsExpanded(true)}
          >
            {`Read more >`}
          </button>
        </RenderIf>
      </div>

      <span className="ml-auto text-xs text-black-500">
        Created {formatLastUpdateAt(createdAt)}{" "}
        <span>{editedAt && `(Edited ${formatLastUpdateAt(editedAt)})`}</span>
      </span>
    </div>
  );
}
